// src/components/PostGrid.tsx
'use client';

import Link from 'next/link';
import { Post } from '@/lib/posts';
import { DEFAULT_COVER_IMAGE } from '@/lib/constants';

interface PostGridProps {
  posts: Post[];
}

export default function PostGrid({ posts }: PostGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-8 gap-y-20">
      {posts.map((post) => (
        <article key={post.slug} className="group">
          {/* Cover Image - Clean & Sharp */}
          <Link href={`/posts/${post.slug}`} className="block bg-white aspect-[3/4] mb-6 overflow-hidden">
            <img
              src={post.frontmatter.coverImage || DEFAULT_COVER_IMAGE}
              alt={post.frontmatter.title}
              className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-70"
              onError={(e) => {
                e.currentTarget.src = DEFAULT_COVER_IMAGE;
              }}
            />
          </Link>

          {/* Content - Minimal Typography */}
          <div className="space-y-3">
            {/* Metadata */}
            <div className="flex items-center space-x-3 text-[#151515] opacity-50">
              <time className="text-xs font-light tracking-[0.1em] uppercase">
                {new Date(post.frontmatter.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC'
                })}
              </time>
              {post.frontmatter.tags?.[0] && (
                <>
                  <span className="w-px h-3 bg-[#151515] opacity-30"></span>
                  <span className="text-xs font-light tracking-[0.1em] uppercase">
                    {post.frontmatter.tags[0]}
                  </span>
                </>
              )}
            </div>

            {/* Title */}
            <h2 className="text-lg font-heading font-normal text-[#151515] leading-tight tracking-tight">
              <Link href={`/posts/${post.slug}`} className="hover:opacity-60 transition-opacity">
                {post.frontmatter.title}
              </Link>
            </h2>
          </div>
        </article>
      ))}
    </div>
  );
}
